import React, { createContext, useContext, useRef, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getProfile, getStats, getActivity } from '../services/creatorService';
import { getCampaigns, getAvailableCampaigns, getTakeoverCampaigns } from '../services/campaignService';
import { getPayments } from '../services/paymentService';

const DataContext = createContext(null);

const CACHE_TTL = 60 * 1000; // 60 seconds

function createCache() {
  return {
    profile: { data: null, ts: 0, loading: null },
    stats: { data: null, ts: 0, loading: null },
    activity: { data: null, ts: 0, loading: null },
    campaigns: { data: null, ts: 0, loading: null },
    availableCampaigns: { data: null, ts: 0, loading: null },
    payments: { data: null, ts: 0, loading: null },
    takeoverCampaigns: { data: null, ts: 0, loading: null },
  };
}

// Background fetch helper — observes failures without hiding them from foreground callers.
function bgFetch(cache, key, fetcher, isCurrentCreator) {
  if (cache.current[key].data || cache.current[key].loading) return;
  const entry = cache.current[key];
  const promise = fetcher()
    .then((res) => {
      if (!isCurrentCreator() || cache.current[key] !== entry) throw new Error('Creator data request was superseded.');
      cache.current[key] = { data: res.data, ts: Date.now(), loading: null };
      return res.data;
    })
    .catch((err) => {
      if (isCurrentCreator() && cache.current[key] === entry) {
        console.warn(`[DataContext] bgFetch "${key}" failed:`, err?.message || err);
        entry.loading = null;
      }
      throw err;
    });
  // Observe background failures, but let a foreground caller sharing this promise retry.
  promise.catch(() => {});
  entry.loading = promise;
}

export function DataProvider({ children }) {
  const cache = useRef(createCache());
  const { consumePrefetch, creator } = useAuth();
  const prefetched = useRef(false);
  const activeCreatorId = useRef(null);
  const creatorIdRef = useRef(creator?.id);
  creatorIdRef.current = creator?.id;

  // Seed cache from auth verify prefetch, then eagerly fetch the rest
  useEffect(() => {
    const creatorId = creator?.id;
    if (!creatorId) {
      cache.current = createCache();
      prefetched.current = false;
      activeCreatorId.current = null;
      return;
    }

    if (activeCreatorId.current && activeCreatorId.current !== creatorId) {
      cache.current = createCache();
      prefetched.current = false;
    }
    activeCreatorId.current = creatorId;

    if (!creator || prefetched.current) return;
    prefetched.current = true;

    const prefetch = consumePrefetch();
    if (prefetch) {
      const now = Date.now();
      if (prefetch.profile && !cache.current.profile.loading) {
        cache.current.profile = { data: prefetch.profile, ts: now, loading: null };
      }
      if (prefetch.stats && !cache.current.stats.loading) {
        cache.current.stats = { data: prefetch.stats, ts: now, loading: null };
      }
    }

    // Eagerly fetch remaining data in background so all pages are instant
    const isCurrentCreator = () => creatorIdRef.current === creatorId;
    bgFetch(cache, 'activity', () => getActivity(10), isCurrentCreator);
    bgFetch(cache, 'campaigns', getCampaigns, isCurrentCreator);
    bgFetch(cache, 'availableCampaigns', getAvailableCampaigns, isCurrentCreator);
    bgFetch(cache, 'payments', getPayments, isCurrentCreator);
    bgFetch(cache, 'takeoverCampaigns', getTakeoverCampaigns, isCurrentCreator);
  }, [creator, consumePrefetch]);

  const fetchCached = useCallback(async (key, fetcher, forceRefresh = false) => {
    const requestCreatorId = creatorIdRef.current;
    // Child page effects may run before this provider's account-change effect.
    if (activeCreatorId.current !== requestCreatorId) {
      cache.current = createCache();
      prefetched.current = false;
      activeCreatorId.current = requestCreatorId;
    }
    const entry = cache.current[key];
    const now = Date.now();
    const isFresh = entry.data && (now - entry.ts < CACHE_TTL) && !forceRefresh;

    if (isFresh) {
      return entry.data;
    }

    if (entry.loading) {
      return entry.loading;
    }

    const promise = fetcher()
      .then((res) => {
        if (creatorIdRef.current !== requestCreatorId || cache.current[key] !== entry) throw new Error('Creator data request was superseded.');
        cache.current[key] = { data: res.data, ts: Date.now(), loading: null };
        return res.data;
      })
      .catch((err) => {
        if (creatorIdRef.current === requestCreatorId && cache.current[key] === entry) entry.loading = null;
        throw err;
      });

    cache.current[key].loading = promise;
    return promise;
  }, []);

  const getCachedProfile = useCallback((force) => fetchCached('profile', getProfile, force), [fetchCached]);
  const getCachedStats = useCallback((force) => fetchCached('stats', getStats, force), [fetchCached]);
  const getCachedActivity = useCallback((force) => fetchCached('activity', () => getActivity(10), force), [fetchCached]);
  const getCachedCampaigns = useCallback((force) => fetchCached('campaigns', getCampaigns, force), [fetchCached]);
  const getCachedAvailableCampaigns = useCallback((force) => fetchCached('availableCampaigns', getAvailableCampaigns, force), [fetchCached]);
  const getCachedPayments = useCallback((force) => fetchCached('payments', getPayments, force), [fetchCached]);
  const getCachedTakeoverCampaigns = useCallback((force) => fetchCached('takeoverCampaigns', getTakeoverCampaigns, force), [fetchCached]);

  const getStale = useCallback((key) => (
    activeCreatorId.current === creatorIdRef.current ? cache.current[key]?.data || null : null
  ), []);

  const invalidate = useCallback((key) => {
    if (key) {
      cache.current[key] = { data: null, ts: 0, loading: null };
    } else {
      cache.current = createCache();
    }
  }, []);

  return (
    <DataContext.Provider value={{
      getCachedProfile, getCachedStats, getCachedActivity,
      getCachedCampaigns, getCachedAvailableCampaigns, getCachedPayments, getCachedTakeoverCampaigns,
      getStale, invalidate
    }}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used within DataProvider');
  return ctx;
}
